/**
 * ModuleTopNav — horizontal sub-navigation band rendered below the
 * page header when the current route belongs to a configured module.
 *
 * Self-hides when:
 *   - the current pathname does not belong to any configured module, OR
 *   - the user can see no items in the active module (fail-closed).
 *
 * Only shows items the current user has permission to access, using the
 * central canSeeNavItem predicate from permissions.ts (same logic as
 * Sidebar.tsx and route guards). This means:
 *   - scope:'platform' items require Me.is_platform_admin (no system:admin bypass).
 *   - system:admin short-circuits all non-platform permission checks.
 *   - Fail-closed: items hidden until /me resolves.
 *
 * Fixes false-affordance bug: platform-only tabs (Screening Console,
 * Integrations, Platform Feature Flags) were previously visible to
 * tenant-admins in the top-nav because itemVisible() did not check
 * platformAdminOnly / scope. Now uses canSeeNavItem which honors scope.
 *
 * A11y:
 *   - `<nav aria-label="…">` landmark so screen-reader users can
 *     jump directly to the section navigation.
 *   - Active link carries aria-current="page".
 *   - Focus styles follow the existing app ring pattern.
 *   - Internal "Skip module navigation" link (WCAG 2.4.1 Option B)
 *     targets #page-content so keyboard users who land in <main> can
 *     bypass the module bar.
 */
import { NavLink, useLocation } from 'react-router-dom'
import { useMe } from '@/features/auth/hooks'
import { cn } from '@/lib/cn'
import { findActiveModule, isItemActive } from '@/lib/navigation'
import { navItemPasses, makeAuthContext } from '@/lib/permissions'
import { usePlatformTenantOptional } from '@/features/platform/context'
import type { ModuleNavItem } from '@/lib/navigation'

export function ModuleTopNav() {
  const { pathname } = useLocation()
  const { data: me } = useMe()

  // Phase 3 (tenant-view switcher): read the selected tenant so the module
  // top nav surfaces tenant-operational items when a platform admin is in
  // tenant-view mode. usePlatformTenantOptional is safe when the provider
  // is absent (standalone test renders).
  // IMPORTANT: this hook must be called BEFORE any early returns (Rules of Hooks).
  const platformTenant = usePlatformTenantOptional()
  const tenantViewId = platformTenant?.selectedTenantId ?? null

  const activeModule = findActiveModule(pathname)

  // No active module for this route — render nothing.
  //
  // Fix S2 (clarity): notable intentional cases where findActiveModule returns
  // undefined and the top nav is therefore absent:
  //   • /blocks/approvals — the canApprove block-approvals queue link is rendered
  //     outside the SECTIONS loop in Sidebar.tsx (it is a conditional affordance
  //     for users with blocks:approve, not a standard Compliance module item).
  //     The "Blocks" Compliance item carries exact:true so /blocks/approvals does
  //     not false-positive activate the Compliance module — this is intentional:
  //     the approval queue is a standalone operator action surface, not a submenu
  //     page of the Compliance module.
  //   • /settings, /platform, /login — flat sidebar sections, no module.
  if (!activeModule) return null

  // Build the central AuthContext. All visibility decisions go through canSeeNavItem.
  const ctx = makeAuthContext(me, tenantViewId)

  // W2 fix (ADR-0024): use navItemPasses instead of canSeeNavItem so that items
  // carrying a featureFlag are correctly gated by the flag result.
  // No module item uses featureFlag yet; navItemPasses degrades gracefully to
  // canSeeNavItem semantics when featureFlag is absent (flag param ignored).
  // If/when featureFlag is added to a module item, resolve the flag with a hook
  // in this component and pass it as the third argument to navItemPasses.
  const visibleItems = activeModule.items.filter((item: ModuleNavItem) =>
    navItemPasses(item, ctx),
  )

  // If the user can't see any item (e.g. new user loading), render nothing.
  if (visibleItems.length === 0) return null

  return (
    <nav
      aria-label={`${activeModule.label} navigation`}
      className="border-b border-border bg-card"
    >
      {/* WCAG 2.4.1 Option B: visually-hidden skip link so keyboard users
          who land in <main> via the outer SkipLink can bypass the module
          bar and jump directly to page content. Targets #page-content which
          is the id on the content wrapper div in AppShell. */}
      <a
        href="#page-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:z-50 focus:rounded focus:bg-bg focus:px-3 focus:py-2 focus:text-sm focus:font-medium focus:text-fg focus:shadow-lg focus:outline focus:outline-2 focus:outline-primary"
      >
        Skip module navigation
      </a>
      <ul className="mx-auto flex max-w-screen-2xl items-center overflow-x-auto whitespace-nowrap px-4 sm:px-6">
        {visibleItems.map((item) => {
          const active = isItemActive(item.path, pathname, item.exact)
          return (
            <li key={item.path}>
              <NavLink
                to={item.path}
                // Pass `end` to NavLink so its internal active-state (which drives
                // aria-current) uses the same matching semantics as our isItemActive
                // helper. Without this, NavLink uses prefix matching by default and
                // would mark /dashboard as active when the current path is /dashboard/hq,
                // even though the item has exact:true.
                end={item.exact === true}
                className={({ isActive: _rlIsActive }) =>
                  cn(
                    // Underline-tab pattern from Tabs.tsx / TabsTrigger.
                    // -mb-px so the bottom border sits on top of the nav border-b.
                    '-mb-px inline-flex items-center border-b-2 px-3 py-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
                    active
                      ? 'border-primary text-fg font-medium'
                      : 'border-transparent text-muted-fg hover:text-fg',
                  )
                }
                aria-current={active ? 'page' : undefined}
              >
                {item.label}
              </NavLink>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}
